<template>
  <v-app id="app">
    <v-navigation-drawer
      v-if="effectiveDrawer"
      v-model="displayMainMenu"
      fixed
      app
      :clipped="$vuetify.breakpoint.lgAndUp"
    >
      <v-list>
        <template v-for="(item, i) in menuItems">
          <component
            :is="item"
            :key="i"
          />
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-model="displayEmployees"
      app
      fixed
      :clipped="$vuetify.breakpoint.lgAndUp"
      disable-resize-watcher
    >
      <EmployeeFilterList />
    </v-navigation-drawer>

    <v-navigation-drawer
      v-model="displayProjects"
      :right="$vuetify.breakpoint.lgAndUp"
      app
      fixed
      :clipped="$vuetify.breakpoint.lgAndUp"
      width="350"
      disable-resize-watcher
    >
      <ProjectFilterList />
    </v-navigation-drawer>

    <v-app-bar
      app
      fixed
      color="primary"
      dark
      :clipped-left="$vuetify.breakpoint.lgAndUp"
      :clipped-right="$vuetify.breakpoint.lgAndUp"
      :class="{fixToolbar: displayProjects }"
      :extension-height="extensionHeight"
    >
      <v-app-bar-nav-icon @click.stop="toggleMainMenu" />

      <template
        v-if="isLoggedIn && $vuetify.breakpoint.mdAndDown"
        v-slot:extension
      >
        <DateRangePicker
          :value="selectedPeriod"
          @input="updatePeriod"
        />
      </template>
      <template v-if="isLoggedIn && $vuetify.breakpoint.lgAndUp">
        <v-spacer />
        <DateRangePicker
          :value="selectedPeriod"
          @input="updatePeriod"
        />
      </template>
      <v-spacer />
      <span v-if="isLoggedIn && $vuetify.breakpoint.mdAndDown">
        <v-btn
          icon
          @click="toggleEmployees"
        >
          <v-badge
            :value="selectedEmployeesNeedHighlighting"
            color="white"
            dot
          >
            <v-icon>mdi-person</v-icon>
          </v-badge>
        </v-btn>

        <v-btn
          icon
          @click="toggleProjects"
        >
          <v-badge
            :value="selectedProjectsNeedHighlighting"
            color="white"
            dot
          >
            <v-icon>mdi-briefcase</v-icon>
          </v-badge>
        </v-btn>
      </span>

      <v-toolbar-items v-if="isLoggedIn && $vuetify.breakpoint.lgAndUp">
        <v-btn
          text
          @click="toggleEmployeesAndProjects"
        >
          <v-badge
            :value="selectedProjectsNeedHighlighting || selectedEmployeesNeedHighlighting"
            color="white"
            dot
          >
            <span> Filter </span>
          </v-badge>
        </v-btn>
      </v-toolbar-items>
    </v-app-bar>

    <v-main>
      <v-container class="fluidContainer">
        <router-view />
      </v-container>
      <v-layout />
    </v-main>

    <v-footer
      app
      inset
    >
      <v-spacer />
      <div>Red Lama | Copyright &copy; 2020 iTerra GmbH</div>
    </v-footer>
    <UpdateWatcher />
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import "@fontsource/roboto/100.css"
import "@fontsource/roboto/300.css"
import "@fontsource/roboto/400.css"
import "@fontsource/roboto/500.css"
import "@fontsource/roboto/700.css"
import "@fontsource/roboto/900.css"

import Tenant from './components/Tenant'
import Menu from './components/sidebar/Menu'
import ViewSettings from './components/sidebar/ViewSettings'
import LoginLogout from './components/sidebar/LoginLogout'
import DateRangePicker from './components/Daterange'
import EmployeeFilterList from './components/EmployeeFilterList'
import ProjectFilterList from './components/ProjectFilterList'
import UpdateWatcher from './UpdateWatcher'

import router from './router'

export default {
  name: 'App',
  router: router,
  components: {
    DateRangePicker,
    EmployeeFilterList,
    ProjectFilterList,
    UpdateWatcher,
  },
  metaInfo: {
    titleTemplate (title) {
      if (title) {
        return `${title} | Red Lama`
      } else {
        return 'Red Lama'
      }
    },
  },
  data () {
    return {
      displayMainMenu: this.isLoggedIn,
      manualDrawer: false,
      internalDisplayEmployees: false,
      internalDisplayProjects: false,
      lastMenuState: false
    }
  },
  computed: {
    displayProjects: {
      get () {
        return this.isLoggedIn && this.internalDisplayProjects
      },
      set (value) {
        this.internalDisplayProjects = value
      },
    },
    displayEmployees: {
      get () {
        return this.isLoggedIn && this.internalDisplayEmployees
      },
      set (value) {
        this.internalDisplayEmployees = value
      },
    },
    lel () {
      return this.$vuetify
    },
    effectiveDrawer () {
      if (this.manualDrawer) {
        return this.displayMainMenu
      } else {
        return this.isLoggedIn
      }
    },
    extensionHeight () {
      if (this.$vuetify.breakpoint.xs) {
        return 48 * 3
      } else {
        return 48
      }
    },
    menuItems () {
      if (this.isLoggedIn) {
        var menu = [Menu,
          Tenant,
          ViewSettings,
          LoginLogout
        ]
        return menu
      } else {
        return [LoginLogout]
      }
    },
    ...mapGetters([
      'employeePermission',
      'isLoggedIn',
      'selectedPeriod',
      'selectedProjects',
      'selectedProjectsNeedHighlighting',
      'selectedEmployees',
      'selectedEmployeesNeedHighlighting',
    ])
  },
  methods: {
    ...mapActions(['updateSelectedPeriod']),
    updatePeriod (value) {
      this.updateSelectedPeriod({
        selection: value.selection,
        start: value.start,
        end: value.end,
      })
    },
    toggleMainMenu () {
      let newState = !this.displayMainMenu
      if (newState) {
        this.displayEmployees = this.displayProjects = false
      }
      this.$nextTick(() => {
         this.displayMainMenu = newState
      });
    },
    toggleEmployeesAndProjects () {
      let newState
      if (this.displayEmployees === this.displayProjects) {
        newState = !this.displayProjects
      } else {
        newState = true
      }

      if (newState) {
        this.lastMenuState = this.displayMainMenu
        this.displayMainMenu = false
        this.$nextTick(() => {
          this.displayEmployees = this.displayProjects = newState
        });
      }
      else {
        this.displayEmployees = this.displayProjects = newState
        this.$nextTick(() => {
          this.displayMainMenu = this.lastMenuState
        });
      }
    },
    toggleEmployees () {
      this.displayEmployees = !this.displayEmployees
    },
    toggleProjects () {
      this.displayProjects = !this.displayProjects
    }
  }
}
</script>

<style css>
html {
  overflow-y: auto;
}
</style>

<style scss scoped>
.fixToolbar {
  padding-right: 0px !important
}
.fluidContainer {
  max-width: 100%;
}
</style>
